import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FpTabs,
  FpTabsContent,
  FpTabsList,
  FpTabsTrigger,
} from "@/components/ui/tabs";
import {
  Timeline,
  TimelineItem,
  TimelineItemLast,
} from "@/components/ui/timeline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useMcpPrompts,
  useMcpPromptsWithCapabilities,
  useMcpResources,
  useMcpResourcesWithCapabilities,
  useMcpTools,
  useMcpToolsWithCapabilities,
} from "@/lib/hooks/useMcpInspector";
import { getProjectQueryOptions } from "@/lib/hooks/useProjects";
import {
  applyPromptUrlParamsToStore,
  applyToolUrlParamsToStore,
} from "@/lib/inspector/navigation";
import {
  type ParsedParam,
  parsePromptArguments,
  parseToolInputSchema,
} from "@/lib/mcp/schema";
import { cn } from "@/lib/utils";
import { useInspectorStore } from "@/stores/inspectorStore";
import { useMcpSessionStore } from "@/stores/mcpSessionStore";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { RefreshCw, Settings } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { InspectorHistorySidebar } from "./inspector/InspectorHistorySidebar";
import { PromptExecutionPanel, PromptSelector } from "./inspector/prompts";
import {
  ResourceSelector,
  ResourceViewer,
  useResourceManagement,
} from "./inspector/resources";
import { buildDefaultValues } from "./inspector/shared/utils/formatting";
import { ToolExecutionPanel, ToolSelector } from "./inspector/tools";

interface ProjectInspectorTabProps {
  projectId: string;
}

function getInputSchema(definition: unknown): unknown {
  if (!definition || typeof definition !== "object") {
    return undefined;
  }
  const t = definition as Record<string, unknown>;
  const input = t.input as Record<string, unknown> | undefined;
  return t.input_schema ?? t.inputSchema ?? input?.schema ?? undefined;
}

export function ProjectInspectorTab({ projectId }: ProjectInspectorTabProps) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/(projects)/projects/$projectId" });

  // Zustand store
  const {
    selectedTool,
    selectedPrompt,
    selectSectionState,
    configureSectionState,
    setSelectedTool,
    setSelectedPrompt,
    getPromptState,
    updatePromptArguments,
  } = useInspectorStore();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    historySidebar: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Load project to determine deployment status
  const { data: projectData, isLoading: projectLoading } = useQuery(
    getProjectQueryOptions(projectId),
  );
  const project = projectData?.project;
  const isDeployed = useMemo(() => {
    if (!project?.latestDeployment) {
      return false;
    }
    // Treat deployment as live when status is complete, regardless of URL presence
    return project.latestDeployment.status === "complete";
  }, [project]);

  // MCP initialization is handled by useProjectMcpSession in ProjectTabs
  // Get initialization state from the capability-aware hooks instead

  // Get session state for fallback logic
  const session = useMcpSessionStore((state) => state.getSession(projectId));

  // Fetch tools/resources with capability-aware hooks (primary)
  const toolsWithCapabilities = useMcpToolsWithCapabilities(
    projectId,
    isDeployed,
  );
  const promptsWithCapabilities = useMcpPromptsWithCapabilities(
    projectId,
    isDeployed,
  );
  const resourcesWithCapabilities = useMcpResourcesWithCapabilities(
    projectId,
    isDeployed,
  );

  // Fallback hooks (secondary) - same logic as CompactInspector
  const fallbackEnabled = isDeployed && session.status === "connected";
  const toolsFallback = useMcpTools(projectId, fallbackEnabled);
  const promptsFallback = useMcpPrompts(projectId, fallbackEnabled);
  const resourcesFallback = useMcpResources(projectId, fallbackEnabled);

  // Use capability-aware data if available, otherwise fallback
  const tools = toolsWithCapabilities.data ?? toolsFallback.data ?? [];
  const prompts = promptsWithCapabilities.data ?? promptsFallback.data ?? [];
  const resources =
    resourcesWithCapabilities.data ?? resourcesFallback.data ?? [];

  // Derived state from the primary hooks
  const toolsLoading =
    toolsWithCapabilities.isLoading ||
    (fallbackEnabled && toolsFallback.isLoading);
  const promptsLoading =
    promptsWithCapabilities.isLoading ||
    (fallbackEnabled && promptsFallback.isLoading);
  const resourcesLoading =
    resourcesWithCapabilities.isLoading ||
    (fallbackEnabled && resourcesFallback.isLoading);

  const toolsError = toolsWithCapabilities.error ?? toolsFallback.error;
  const promptsError = promptsWithCapabilities.error ?? promptsFallback.error;
  const resourcesError =
    resourcesWithCapabilities.error ?? resourcesFallback.error;

  const hasToolsCapability = toolsWithCapabilities.hasToolsCapability;
  const hasPromptsCapability = promptsWithCapabilities.hasPromptsCapability;
  const hasResourcesCapability =
    resourcesWithCapabilities.hasResourcesCapability;

  // Debug logging for development
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (tools.length > 0 || prompts.length > 0 || resources.length > 0) {
        console.log("ProjectInspectorTab Debug - Data found:", {
          tools: tools.length,
          prompts: prompts.length,
          resources: resources.length,
          sessionStatus: session.status,
          isDeployed,
          fallbackEnabled,
          capabilityData: toolsWithCapabilities.data?.length || 0,
          fallbackData: toolsFallback.data?.length || 0,
          hasToolsCapability,
          showToolsTab:
            !mcpInitError &&
            (mcpInitLoading || hasToolsCapability || tools.length > 0),
          mcpInitLoading,
          mcpInitError: mcpInitError?.toString(),
        });
      } else if (
        isDeployed &&
        !toolsLoading &&
        !promptsLoading &&
        !resourcesLoading
      ) {
        console.log("ProjectInspectorTab Debug - No data:", {
          sessionStatus: session.status,
          isDeployed,
          fallbackEnabled,
          toolsError: toolsError?.toString(),
          promptsError: promptsError?.toString(),
          hasToolsCapability,
        });
      }
    }, [
      tools.length,
      prompts.length,
      resources.length,
      session.status,
      isDeployed,
      fallbackEnabled,
      toolsLoading,
      promptsLoading,
      resourcesLoading,
      toolsError,
      promptsError,
      hasToolsCapability,
      toolsWithCapabilities.data,
      toolsFallback.data,
    ]);
  }

  // Resource management
  const resourceManagement = useResourceManagement({
    resources: resources || null,
    error: resourcesError,
  });

  // Helper function to handle tool selection with URL sync
  const handleToolSelection = (toolName: string | null) => {
    // Update the store
    setSelectedTool(toolName);

    // Update the URL to reflect the new selection
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: {
        ...search,
        selectedTool: toolName || undefined,
        // Clear params when switching tools
        params: undefined,
        selectedPrompt: toolName ? undefined : search.selectedPrompt,
        promptParams: toolName ? undefined : search.promptParams,
      },
    });
  };

  const handlePromptSelection = (promptName: string | null) => {
    setSelectedPrompt(promptName);

    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: {
        ...search,
        selectedPrompt: promptName || undefined,
        promptParams: undefined,
        selectedTool: promptName ? undefined : search.selectedTool,
        params: promptName ? undefined : search.params,
      },
    });
  };

  const selectedToolDef = useMemo(
    () => (tools || []).find((t) => t.name === (selectedTool || "")),
    [tools, selectedTool],
  );
  const selectedPromptDef = useMemo(
    () => (prompts || []).find((p) => p.name === (selectedPrompt || "")),
    [prompts, selectedPrompt],
  );

  // Determine loading state - use the same logic as CompactInspector
  // Only care about actual data query loading, not initialization loading
  const mcpInitLoading = toolsLoading || promptsLoading || resourcesLoading;
  const mcpInitError = toolsError || promptsError || resourcesError;

  // Show tabs based on actual data availability (not just capabilities)
  // This ensures fallback data is still displayed even if capabilities aren't declared
  const showToolsTab =
    !mcpInitError && (mcpInitLoading || hasToolsCapability || tools.length > 0);
  const showPromptsTab =
    !mcpInitError &&
    (mcpInitLoading || hasPromptsCapability || prompts.length > 0);
  const showResourcesTab =
    !mcpInitError &&
    (mcpInitLoading || hasResourcesCapability || resources.length > 0);
  const availableTabs = [
    showToolsTab ? "tools" : null,
    showPromptsTab ? "prompts" : null,
    showResourcesTab ? "resources" : null,
  ].filter(Boolean) as Array<"tools" | "prompts" | "resources">;
  const defaultTab = availableTabs[0] ?? "tools";

  // Determine active tab based on URL parameters
  const activeTab = useMemo(() => {
    // Check for specific tool/prompt/resource selections first (these take priority)
    if (search.selectedTool && availableTabs.includes("tools")) {
      return "tools";
    }
    if (search.selectedPrompt && availableTabs.includes("prompts")) {
      return "prompts";
    }
    if (search.selectedResource && availableTabs.includes("resources")) {
      return "resources";
    }
    // Then check for direct tab parameter
    if (
      search.inspectorTab &&
      availableTabs.includes(
        search.inspectorTab as "tools" | "prompts" | "resources",
      )
    ) {
      return search.inspectorTab as "tools" | "prompts" | "resources";
    }
    return defaultTab;
  }, [
    search.selectedTool,
    search.selectedPrompt,
    search.selectedResource,
    search.inspectorTab,
    availableTabs,
    defaultTab,
  ]);

  const tabListCols =
    availableTabs.length === 1
      ? "grid-cols-1"
      : availableTabs.length === 2
        ? "grid-cols-2"
        : "grid-cols-3";
  const tabLabels: Record<"tools" | "prompts" | "resources", string> = {
    tools: "Tools",
    prompts: "Prompts",
    resources: "Resources",
  };

  const parsedParams: ParsedParam[] = useMemo(() => {
    const schema = getInputSchema(selectedToolDef as unknown);
    return parseToolInputSchema(schema);
  }, [selectedToolDef]);

  const parsedPromptParams: ParsedParam[] = useMemo(() => {
    return parsePromptArguments(selectedPromptDef as unknown);
  }, [selectedPromptDef]);

  // Handle URL parameter reading and store population on mount/navigation
  useEffect(() => {
    const urlSelectedTool = search.selectedTool;
    const urlParams = search.params;

    // Only apply URL state if we have tools available (through capabilities or fallback)
    if (!tools || tools.length === 0) {
      return;
    }

    // If URL specifies a tool that differs from store, update store
    if (urlSelectedTool && urlSelectedTool !== selectedTool) {
      // Verify the tool exists in the available tools
      const toolExists = tools.some((tool) => tool.name === urlSelectedTool);
      if (toolExists) {
        // Update the selected tool in the store
        setSelectedTool(urlSelectedTool);

        // Apply URL parameters if provided (use setTimeout to ensure tool selection completes first)
        if (urlParams) {
          setTimeout(() => {
            applyToolUrlParamsToStore(urlSelectedTool, urlParams);
          }, 0);
        }
      }
    } else if (
      urlSelectedTool &&
      urlSelectedTool === selectedTool &&
      urlParams
    ) {
      // Tool is already selected, just apply parameters
      applyToolUrlParamsToStore(urlSelectedTool, urlParams);
    }
  }, [
    search.selectedTool,
    search.params,
    tools,
    selectedTool,
    setSelectedTool,
  ]);

  useEffect(() => {
    const urlSelectedPrompt = search.selectedPrompt;
    const urlPromptParams = search.promptParams;

    if (!hasPromptsCapability || !prompts || prompts.length === 0) {
      return;
    }

    if (urlSelectedPrompt && urlSelectedPrompt !== selectedPrompt) {
      const promptExists = prompts.some(
        (prompt) => prompt.name === urlSelectedPrompt,
      );

      if (promptExists) {
        setSelectedPrompt(urlSelectedPrompt);

        if (urlPromptParams) {
          applyPromptUrlParamsToStore(urlSelectedPrompt, urlPromptParams);
        }
      }
    }
  }, [
    search.selectedPrompt,
    search.promptParams,
    hasPromptsCapability,
    prompts,
    selectedPrompt,
    setSelectedPrompt,
  ]);

  // Handle URL-based resource selection
  useEffect(() => {
    const urlSelectedResource = search.selectedResource;

    if (!hasResourcesCapability || !resources || resources.length === 0) {
      return;
    }

    // Apply URL resource selection if different from current store selection
    if (
      urlSelectedResource &&
      urlSelectedResource !== resourceManagement.selectedResource
    ) {
      const resourceExists = resources.some(
        (r) => r.uri === urlSelectedResource,
      );
      if (resourceExists) {
        resourceManagement.onResourceSelect(urlSelectedResource);
      }
    }
  }, [
    search.selectedResource,
    hasResourcesCapability,
    resources,
    resourceManagement,
  ]);

  const defaultPromptValues: Record<string, string> = useMemo(() => {
    return buildDefaultValues(parsedPromptParams);
  }, [parsedPromptParams]);

  const lastPromptRef = useRef<string | null>(null);
  if (lastPromptRef.current !== selectedPrompt && selectedPrompt) {
    lastPromptRef.current = selectedPrompt;
    const currentArgs = getPromptState(selectedPrompt).arguments;
    if (Object.keys(currentArgs).length === 0) {
      updatePromptArguments(selectedPrompt, defaultPromptValues);
    }
  }

  // Loading state while fetching project
  if (projectLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>MCP Inspector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-6 w-48 bg-muted animate-pulse rounded mb-2" />
          <div className="h-24 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  // Not deployed gate
  if (!isDeployed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>MCP Inspector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            This project doesn’t have a live deployment yet. Deploy your app to
            enable the MCP Inspector.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 @container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">MCP Inspector</h2>
          <p className="text-muted-foreground">
            Interactive tool and resource testing interface
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Not working yet</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Not working yet</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 @3xl:grid-cols-[2fr_400px] gap-6">
        {/* Inspector Content */}
        <div className="min-w-0">
          <FpTabs
            value={activeTab}
            className="w-full"
            onValueChange={(value) => {
              // Navigate to update the URL with the new tab
              const newSearch: typeof search = { ...search };

              // Clear tool/prompt/resource selections if switching away from their tabs
              if (value !== "tools" && search.selectedTool) {
                handleToolSelection(null);
                newSearch.selectedTool = undefined;
                newSearch.params = undefined;
              }

              if (value !== "prompts" && search.selectedPrompt) {
                handlePromptSelection(null);
                newSearch.selectedPrompt = undefined;
                newSearch.promptParams = undefined;
              }

              if (value !== "resources" && search.selectedResource) {
                resourceManagement.onResourceSelect("");
                newSearch.selectedResource = undefined;
              }

              // Set the inspector tab
              newSearch.inspectorTab = value as
                | "tools"
                | "prompts"
                | "resources";

              navigate({
                to: "/projects/$projectId",
                params: { projectId },
                search: newSearch,
              });
            }}
          >
            {availableTabs.length > 0 ? (
              <FpTabsList
                className={cn("w-full h-auto flex border-b mb-4", tabListCols)}
              >
                {availableTabs.map((tabKey) => (
                  <FpTabsTrigger key={tabKey} value={tabKey}>
                    {tabLabels[tabKey]}
                  </FpTabsTrigger>
                ))}
              </FpTabsList>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <div className="mb-2">No MCP capabilities available</div>
                <div className="text-sm">
                  This server doesn't support tools, prompts, or resources
                </div>
              </div>
            )}

            {showToolsTab && (
              <FpTabsContent value="tools" className="space-y-6">
                {/* Handle MCP initialization and capabilities */}
                {toolsLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                      <span>Loading...</span>
                    </div>
                  </div>
                )}

                {toolsError && (
                  <div className="text-center py-12">
                    <div className="text-destructive mb-2">
                      Failed to load tools
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {String(toolsError)}
                    </div>
                  </div>
                )}

                {!toolsLoading &&
                  !toolsError &&
                  !hasToolsCapability &&
                  tools.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-muted-foreground mb-2">
                        Tools not supported
                      </div>
                      <div className="text-sm text-muted-foreground">
                        This MCP server does not advertise tools capability
                      </div>
                    </div>
                  )}

                {!toolsLoading &&
                  !toolsError &&
                  (hasToolsCapability || tools.length > 0) && (
                    <Timeline>
                      {/* Select Tool Section */}
                      <TimelineItem
                        state={selectSectionState}
                        title="Select Tool"
                        onClick={() => {
                          // Allow clicking back to tool selection if completed
                          if (selectSectionState === "completed") {
                            // Don't change state, just scroll to section
                          }
                        }}
                      >
                        <ToolSelector
                          tools={tools || null}
                          selectedTool={selectedTool}
                          onToolSelect={handleToolSelection}
                          isLoading={toolsLoading}
                          error={toolsError}
                        />
                      </TimelineItem>

                      {/* Configure & Execute Section */}
                      <TimelineItemLast
                        state={configureSectionState}
                        title="Configure & Execute"
                      >
                        <ToolExecutionPanel
                          sessionId={projectId}
                          toolName={selectedTool}
                          parsedParams={parsedParams}
                          configureSectionState={configureSectionState}
                        />
                      </TimelineItemLast>
                    </Timeline>
                  )}
              </FpTabsContent>
            )}

            {showPromptsTab && (
              <FpTabsContent value="prompts" className="space-y-6">
                {promptsLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                      <span>Loading...</span>
                    </div>
                  </div>
                )}

                {promptsError && (
                  <div className="text-center py-12">
                    <div className="text-destructive mb-2">
                      Failed to initialize MCP connection
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {String(promptsError)}
                    </div>
                  </div>
                )}

                {!promptsLoading &&
                  !promptsError &&
                  !hasPromptsCapability &&
                  prompts.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-muted-foreground mb-2">
                        Prompts not supported
                      </div>
                      <div className="text-sm text-muted-foreground">
                        This MCP server does not advertise prompts capability
                      </div>
                    </div>
                  )}

                {!promptsLoading &&
                  !promptsError &&
                  (hasPromptsCapability || prompts.length > 0) && (
                    <Timeline>
                      {/* Select Prompt Section */}
                      <TimelineItem
                        state={selectSectionState}
                        title="Select Prompt"
                        onClick={() => {
                          // Allow clicking back to prompt selection if completed
                          if (selectSectionState === "completed") {
                            // Don't change state, just scroll to section
                          }
                        }}
                      >
                        <PromptSelector
                          prompts={prompts || null}
                          selectedPrompt={selectedPrompt}
                          onPromptSelect={handlePromptSelection}
                          isLoading={promptsLoading}
                          error={promptsError}
                        />
                      </TimelineItem>

                      {/* Configure & Execute Section */}
                      <TimelineItemLast
                        state={configureSectionState}
                        title="Configure & Execute"
                      >
                        <PromptExecutionPanel
                          projectId={projectId}
                          promptName={selectedPrompt}
                          parsedParams={parsedPromptParams}
                          configureSectionState={configureSectionState}
                        />
                      </TimelineItemLast>
                    </Timeline>
                  )}
              </FpTabsContent>
            )}

            {showResourcesTab && (
              <FpTabsContent value="resources" className="space-y-4">
                {/* Handle MCP initialization and capabilities */}
                {resourcesLoading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                      <span>Loading...</span>
                    </div>
                  </div>
                )}

                {resourcesError && (
                  <div className="text-center py-12">
                    <div className="text-destructive mb-2">
                      Failed to initialize MCP connection
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {String(resourcesError)}
                    </div>
                  </div>
                )}

                {!resourcesLoading &&
                  !resourcesError &&
                  !hasResourcesCapability &&
                  resources.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-muted-foreground mb-2">
                        Resources not supported
                      </div>
                      <div className="text-sm text-muted-foreground">
                        This MCP server does not advertise resources capability
                      </div>
                    </div>
                  )}

                {!resourcesLoading &&
                  !resourcesError &&
                  (hasResourcesCapability || resources.length > 0) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <ResourceSelector
                        resources={resources || null}
                        selectedResource={resourceManagement.selectedResource}
                        onResourceSelect={resourceManagement.onResourceSelect}
                        isLoading={resourcesLoading}
                        error={resourcesError}
                        isExpanded={
                          resourceManagement.expandedSections.resources
                        }
                        onToggleExpanded={() =>
                          resourceManagement.onToggleSection("resources")
                        }
                        resourcesNotSupported={
                          resourceManagement.resourcesNotSupported
                        }
                      />
                      <ResourceViewer
                        selectedResource={
                          resourceManagement.selectedResourceData
                        }
                        projectId={projectId}
                      />
                    </div>
                  )}
              </FpTabsContent>
            )}
          </FpTabs>
        </div>

        {/* History Sidebar */}
        <div>
          <InspectorHistorySidebar
            projectId={projectId}
            isExpanded={expandedSections.historySidebar || false}
            onToggleExpanded={() => toggleSection("historySidebar")}
          />
        </div>
      </div>
    </div>
  );
}
